import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, WarningCircle } from '@phosphor-icons/react';

import { MarketShell } from '../../../design-system/components/shells';
import {
  Button,
  Empty,
  PageHeader,
  Pill,
  SkeletonRow,
} from '../../../design-system/components/desktop';
import {
  CartLayout,
  CartLine,
  CartLines,
  CartSellerGroup,
  CartSummary,
  GroupTotal,
  SummaryNote,
  SummaryRow,
  SummaryTotal,
} from '../../../design-system/patterns/cart';
import { routes } from '../../../routes/routes';
import { distinguishingFinish } from '../../../utils/finish';

/**
 * Cart, the view. Pure: every input is a prop, every output is a callback.
 *
 * The container (`CartPage.jsx`) owns the cart context, the fresh-listings
 * merge, localStorage, the stock guards, the auth gate and the handoff to
 * `/checkout`. This file owns what a buyer sees, which is why the state
 * explorer can mount it with fixture props and get the real screen back.
 *
 * WHAT WENT AWAY
 * --------------
 * - The `matchMedia("(max-width: 700px)")` fork. `CartPage.jsx` used to render
 *   a COMPLETE second markup tree below 700px, duplicating the seller header,
 *   the item list, the totals block and a byte-identical 55-line checkout click
 *   handler. There is now one tree, and the only threshold on this page is
 *   `useViewport()`'s 1024 inside the shell and the cart pattern.
 * - `CartPage.css`, 751 lines, and every class name in it.
 * - `Layout` from `SellerDashboard/`, a seller-console chrome wrapped around a
 *   buying page. The buying shell is `MarketShell`.
 * - `TopBar` / `TopBarRight` / `SecureCheckout` / `CategorySelect`. The topbar
 *   rendered a dead "Cart" word, an inert "Buying Tips" label and a hardcoded
 *   "Secure Checkout" lock; `CategorySelect` was already commented out and its
 *   options were literally `MjA0NzM5MDIy` and "Home & Kitchen".
 * - `SellerSection`'s hand-drawn green shield SVG and its `rating / 20`
 *   fallback. The score is now the shared `Rating`, on a real 5-point scale.
 * - The hand-rolled `CardSkeleton` from the marketplace tree.
 *
 * SHIPPING, OPEN PRODUCT QUESTION 1
 * ---------------------------------
 * The cart deliberately shows NO shipping figure and says so. That is what the
 * app does today: the courier rate is only quoted at checkout, by the server,
 * after an address is confirmed. Nothing here names a courier, an origin city
 * or a price. See `Checkout` for the other half of this question.
 *
 * NO PROMO CODE. The app has no code-redemption feature anywhere, so there is
 * no input for one and none may be added. NO "SAVED FOR LATER" either: the
 * wishlist is the real keep-for-later surface.
 *
 * CHECKOUT IS PER SELLER, and that is the app's real model, not a simplification
 * here. `orderItems_<user>` holds ONE seller's bucket, `/checkout` places one
 * order against one seller, so the commit lives in each seller group's footer.
 * The summary on the right totals the whole cart and does not pretend there is
 * a single-click multi-seller checkout.
 */

/* ONE DELIBERATE PREFIX CROSSING, the same one MarketplaceGrid.jsx reports:
   there is no `ds-*` skeleton and no `ds-*` empty state in the library, only
   `.dsk-skeleton*` and `.dsk-empty`, so `SkeletonRow` and `Empty` render below
   1024 too. The fix belongs upstream as `.ds-skeleton` / `.ds-empty`, not as a
   fourteenth hand-rolled skeleton here. */

export function CartPageView({
  sellers = [],
  itemCount = 0,
  cartTotal,
  loading = false,
  error = false,
  onRetry,
  onIncrease,
  onDecrease,
  onRemove,
  onCheckout,
  skeletonCount = 3,
  /* Whether Sleevr is shipping at all, from `useFulfilmentMode` in the
     container. In in-person-only mode there is no courier rate and no
     delivery address to confirm, so the note below described a checkout step
     that does not exist. Defaults to the honest silence: say nothing about
     shipping unless told shipping is on. */
  shipsNationwide = false,
}) {
  const sellerCount = sellers.length;

  const shell = (children) => (
    <MarketShell
      title="Cart"
      activeNav="cart"
      breadcrumbs={[{ label: 'Marketplace', to: routes.MARKETPLACE }, { label: 'Cart' }]}
      back={{ to: routes.MARKETPLACE, label: 'Back to marketplace' }}
    >
      {children}
    </MarketShell>
  );

  if (loading) {
    return shell(
      <>
        <PageHeader title="Cart" sub="Refreshing prices and stock" />
        <div aria-busy="true">
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <SkeletonRow key={index} />
          ))}
        </div>
      </>,
    );
  }

  if (error) {
    return shell(
      <>
        <PageHeader title="Cart" />
        <Empty
          tone="error"
          /* A failed read is a failed read. `ShoppingCart` said "cart" where
             the glyph has to say "this broke". */
          icon={WarningCircle}
          illustration="request-failed"
          title="We could not load your cart"
          text="Something went wrong fetching your items. Your cart is safe, this is just the read."
          actions={<Button onClick={onRetry}>Try again</Button>}
        />
      </>,
    );
  }

  if (sellerCount === 0) {
    return shell(
      <>
        <PageHeader title="Cart" />
        <Empty
          icon={ShoppingCart}
          illustration="cart-is-empty"
          title="Your cart is empty"
          text="Nothing sleeved up yet. Browse trading card singles and sealed product from verified South African sellers."
          actions={
            <Button as={Link} to={routes.MARKETPLACE} size="lg">
              Browse marketplace
            </Button>
          }
        />
      </>,
    );
  }

  return shell(
    <>
      <PageHeader
        title="Cart"
        sub={`${itemCount} ${itemCount === 1 ? 'item' : 'items'} from ${sellerCount} ${
          sellerCount === 1 ? 'seller' : 'sellers'
        }`}
        actions={
          <Button as={Link} to={routes.MARKETPLACE} variant="ghost" size="sm">
            Continue shopping
          </Button>
        }
      />

      <CartLayout>
        <div>
          {sellers.map((seller) => (
            <CartSellerGroup
              key={seller.id}
              storeName={seller.storeName}
              storeHref={seller.storeHref}
              rating={seller.rating}
              sub={seller.joinedLabel}
              action={
                seller.storeHref ? (
                  <Button as={Link} to={seller.storeHref} variant="ghost" size="sm">
                    Visit store
                  </Button>
                ) : null
              }
              footer={
                <>
                  <GroupTotal label="Total, excluding shipping" value={seller.total} />
                  <Button size="lg" onClick={() => onCheckout && onCheckout(seller.id)}>
                    Checkout
                  </Button>
                </>
              }
            >
              <CartLines>
                {seller.items.map((item, index) => (
                  <CartLine
                    key={item.id}
                    image={item.image}
                    imageAlt={item.name}
                    set={item.set}
                    name={item.name}
                    href={item.href}
                    /* Condition and finish share this slot: `CartLine` takes a
                       node, and the two facts belong on one meta line. Finish
                       only when it distinguishes — a "Normal" pill on every
                       line is noise on a surface this dense. */
                    condition={
                      <>
                        {item.condition ? <Pill fit>{item.condition}</Pill> : null}
                        {distinguishingFinish(item.finish) ? (
                          <Pill fit>{distinguishingFinish(item.finish)}</Pill>
                        ) : null}
                      </>
                    }
                    cardNumber={item.cardNumber}
                    note={
                      item.note ? (
                        <Pill tone={item.noteTone || 'warning'}>{item.note}</Pill>
                      ) : null
                    }
                    unitPrice={item.unitPrice}
                    quantity={item.quantity}
                    max={item.max}
                    lineTotal={item.lineTotal}
                    unavailable={item.unavailable}
                    onIncrease={() => onIncrease && onIncrease(seller.id, index)}
                    onDecrease={() => onDecrease && onDecrease(seller.id, index)}
                    onRemove={() => onRemove && onRemove(seller.id, index)}
                  />
                ))}
              </CartLines>
            </CartSellerGroup>
          ))}
        </div>

        <CartSummary title="Order summary" sticky>
          {sellers.map((seller) => (
            <SummaryRow key={seller.id} label={seller.storeName} value={seller.total} />
          ))}

          {/* SHIPPING IS NOT SHOWN HERE, and that is deliberate. The rate is
              quoted by the server at checkout once an address is confirmed,
              so any figure on this page would be invented. Open product
              question 1 in CONVERSION-MAP.md.

              It used to say so in a `SummaryRow` labelled "Shipping" whose
              value was the sentence "Calculated at checkout", which put a
              sentence in the column every other row fills with a Rand amount.
              The subtotal is now the last thing in the money column, and the
              two facts that are NOT amounts — why there is no total, and why
              there is no single checkout button — are prose below it. */}
          <SummaryTotal label="Subtotal" value={cartTotal} />

          <SummaryNote>
            {shipsNationwide
              ? 'Shipping is not included. Your courier rate is quoted at checkout, once you have confirmed a delivery address. Each seller is checked out separately.'
              : 'You collect from the seller in person and pay them when you meet. Each seller is checked out separately.'}
          </SummaryNote>
        </CartSummary>
      </CartLayout>
    </>,
  );
}

export default CartPageView;
